import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, tap, map } from "rxjs/operators";
import { VdiRequest, VdiResponse } from "../interface/vdi-request";
import { environment } from "src/environments/environment";
import { ApiAdapterService } from "./api-adapter.service";

@Injectable({
  providedIn: "root",
})
export class VdiService {
  private apiUrl = environment.apiEndpoint;
  private logPrefix = "[VDI-Service]";
  private useN8nIntegration = false;

  constructor(private http: HttpClient, private apiAdapter: ApiAdapterService) {
    this.log("VDI Service initialized");
  }

  /**
   * Submit VDI request to the Enterprise Backend API
   * @param vdiRequest The VDI request data
   * @returns Observable<VdiResponse>
   */
  submitVdiRequest(vdiRequest: VdiRequest): Observable<VdiResponse> {
    this.log("Submitting VDI request via Enterprise Backend", vdiRequest);

    // Use the API adapter to handle enterprise backend format
    return this.apiAdapter.createVdiRequest(vdiRequest).pipe(
      map((response: any) => {
        // Transform adapter response to VdiResponse format
        const vdiResponse: VdiResponse = {
          result: response.data.success || false,
          message:
            response.data.message ||
            response.message ||
            "VDI request processed",
          error: response.data.success
            ? undefined
            : response.data.error || "Request failed",
        };
        return vdiResponse;
      }),
      tap((response) => {
        this.log(
          "VDI request submitted successfully via Enterprise Backend",
          response
        );
        this.logUserActivity("VDI_REQUEST_SUBMITTED", {
          username: vdiRequest.username,
          software: vdiRequest.software || "Office 365",
          startDateTime: vdiRequest.startDateTime,
          endDateTime: vdiRequest.endDateTime,
          success: response.result,
        });
      }),
      catchError((error) => {
        this.logError(
          "Failed to submit VDI request via Enterprise Backend",
          error
        );
        this.logUserActivity("VDI_REQUEST_FAILED", {
          username: vdiRequest.username,
          softwareSet: vdiRequest.softwareSet,
          error: error.message,
          success: false,
        });
        return throwError(() => new Error(this.getErrorMessage(error)));
      })
    );
  }

  // Removed: external workflow submission
  // Removed: submitVdiRequestViaWorkflow

  /** Submit VDI request (direct API only) */
  submitVdiRequestWithOption(
    vdiRequest: VdiRequest,
    useWorkflow: boolean = false
  ): Observable<VdiResponse> {
    this.log("Submitting VDI request");
    return this.submitVdiRequest(vdiRequest);
  }

  /**
   * Test VDI request endpoint (for testing purposes)
   * @param vdiRequest The VDI request data
   * @returns Observable<VdiResponse>
   */
  testVdiRequest(vdiRequest: VdiRequest): Observable<VdiResponse> {
    this.log("Testing VDI request", vdiRequest);

    // Use access_token from request if provided; fallback to default key
    const apiKey = vdiRequest.access_token || "vdi-moodle-integration-key";
    const headers = new HttpHeaders({
      "X-API-KEY": apiKey,
      "Content-Type": "application/json",
    });

    const url = `${this.apiUrl}api/external/mike-CreateRequest`;

    // Normalize payload: convert epoch seconds to ISO 8601 if needed
    const toIsoIfEpoch = (value: string | undefined): string | undefined => {
      if (!value) return value;
      const num = Number(value);
      if (!isNaN(num) && value.length >= 10 && value.length <= 13) {
        const millis = value.length === 13 ? num : num * 1000;
        return new Date(millis).toISOString();
      }
      return value;
    };

    // Prepare payload with normalized dates and field name alignment
    const payload: any = { ...vdiRequest };
    payload.startDateTime = toIsoIfEpoch(payload.startDateTime);
    payload.endDateTime = toIsoIfEpoch(payload.endDateTime);

    // Map "Comments" -> "remarks" if present (backend expects "remarks")
    if (payload.Comments && !payload.remarks) {
      payload.remarks = payload.Comments;
      delete payload.Comments;
    }

    this.log("VDI test request payload", payload);

    return this.http.post<VdiResponse>(url, payload, { headers }).pipe(
      tap((response) => {
        this.log("VDI test request completed successfully", response);
        this.logUserActivity("VDI_TEST_REQUEST_COMPLETED", {
          username: payload.username,
          softwareSet: payload.softwareSet,
          success: true,
        });
      }),
      catchError((error) => {
        this.logError("Failed to test VDI request", error);
        this.logUserActivity("VDI_TEST_REQUEST_FAILED", {
          username: payload.username,
          softwareSet: payload.softwareSet,
          error: error.message,
          success: false,
        });
        return throwError(() => new Error(this.getErrorMessage(error)));
      })
    );
  }

  getVmDetails(ticketId: number | string): Observable<any> {
    this.log("Fetching VM details", { ticketId });

    const url = `${this.apiUrl}api/external/mike-GetVMDetails`;
    const payload = { ticketId };

    // #region agent log (debug-session)
    fetch('https://localhost:7182/ingest/67a97cca-a992-499c-a645-9a0d2ff80c1a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'vdi.service.ts:getVmDetails(entry)',message:'getVmDetails called',data:{ticketId:String(ticketId),url},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion

    return this.http.post<any>(url, payload).pipe(
      tap((response) => {
        this.log("VM details retrieved", response);
        // #region agent log (debug-session)
        fetch('https://localhost:7182/ingest/67a97cca-a992-499c-a645-9a0d2ff80c1a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'vdi.service.ts:getVmDetails(tap)',message:'getVmDetails response received',data:{ticketId:String(ticketId),responseType:typeof response,responseKeys:(response && typeof response==='object')?Object.keys(response).slice(0,20):[],hasSuccessProp:response?.success!==undefined||response?.data?.success!==undefined,hasMessageProp:response?.message!==undefined||response?.data?.message!==undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
      }),
      catchError((error) => {
        this.logError("Failed to fetch VM details", error);
        // #region agent log (debug-session)
        fetch('https://localhost:7182/ingest/67a97cca-a992-499c-a645-9a0d2ff80c1a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'vdi.service.ts:getVmDetails(catchError)',message:'getVmDetails request failed',data:{ticketId:String(ticketId),status:error?.status ?? null,errorMessage:error?.message ?? 'unknown'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        return throwError(() => new Error(this.getErrorMessage(error)));
      })
    );
  }

  // Removed: test via external workflow
  // Removed: testVdiRequestViaWorkflow

  // Removed: external workflow integration helpers
  // Removed: isN8nIntegrationEnabled

  // Removed: getN8nIntegrationStatus()

  /**
   * Submit VDI request via workflow (compatibility method)
   * @param vdiRequest The VDI request data
   * @param isTest Whether this is a test request
   * @returns Observable<VdiResponse>
   */
  submitVdiRequestViaWorkflow(
    vdiRequest: VdiRequest,
    isTest: boolean = false
  ): Observable<VdiResponse> {
    this.log(
      "submitVdiRequestViaWorkflow called (compatibility mode) - redirecting to direct API"
    );
    // Redirect to direct API submission since workflow integration is removed
    if (isTest) {
      return this.testVdiRequest(vdiRequest);
    } else {
      return this.submitVdiRequest(vdiRequest);
    }
  }

  /**
   * Get API health status
   * @returns Observable<any>
   */
  getHealthStatus(): Observable<any> {
    this.log("Checking API health status");

    const url = `${this.apiUrl}api/external/health`;

    return this.http.get(url).pipe(
      tap((response) => {
        this.log("API health check completed", response);
      }),
      catchError((error) => {
        this.logError("API health check failed", error);
        return throwError(() => new Error("API health check failed"));
      })
    );
  }

  /**
   * Validate access token
   * @param token The access token to validate
   * @returns Observable<boolean>
   */
  validateToken(token: string): Observable<boolean> {
    this.log("Validating access token");

    // Simple token validation - in production, this would call a backend endpoint
    if (!token || token.length < 10) {
      this.logError("Invalid token format");
      return of(false);
    }

    // For demo purposes, accept any token that looks like a valid format
    const isValid =
      /^[a-zA-Z0-9-_]+\.[a-zA-Z0-9-_]+\.[a-zA-Z0-9-_]+$/.test(token) ||
      token.length >= 20;

    this.log("Token validation result", { isValid });
    return of(isValid);
  }

  /**
   * Log general information
   * @param message The log message
   * @param data Optional data to log
   */
  private log(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: "INFO",
      service: "VDI",
      message,
      data: data || null,
    };

    console.log(`${this.logPrefix} ${message}`, data || "");

    // Store in local storage for debugging
    this.storeLog(logEntry);
  }

  /**
   * Log error information
   * @param message The error message
   * @param error The error object
   */
  private logError(message: string, error?: any): void {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: "ERROR",
      service: "VDI",
      message,
      error: {
        message: error?.message || "Unknown error",
        status: error?.status || "Unknown status",
        url: error?.url || "Unknown URL",
      },
    };

    console.error(`${this.logPrefix} ${message}`, error || "");

    // Store in local storage for debugging
    this.storeLog(logEntry);
  }

  /**
   * Log user activity
   * @param action The action performed
   * @param details Additional details
   */
  private logUserActivity(action: string, details: any): void {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: "ACTIVITY",
      service: "VDI",
      action,
      details,
      userId: details.username || "anonymous",
    };

    console.log(`${this.logPrefix} User Activity: ${action}`, details);

    // Store in local storage for debugging
    this.storeLog(logEntry);
  }

  /**
   * Store log entry in local storage
   * @param logEntry The log entry to store
   */
  private storeLog(logEntry: any): void {
    try {
      const existingLogs = JSON.parse(localStorage.getItem("vdi-logs") || "[]");
      existingLogs.push(logEntry);

      // Keep only last 100 log entries
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }

      localStorage.setItem("vdi-logs", JSON.stringify(existingLogs));
    } catch (error) {
      console.error("Failed to store log entry", error);
    }
  }

  /**
   * Get stored logs
   * @returns Array of log entries
   */
  getLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem("vdi-logs") || "[]");
    } catch (error) {
      console.error("Failed to retrieve logs", error);
      return [];
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    try {
      localStorage.removeItem("vdi-logs");
      this.log("Logs cleared");
    } catch (error) {
      console.error("Failed to clear logs", error);
    }
  }

  /**
   * Get user-friendly error message
   * @param error The error object
   * @returns User-friendly error message
   */
  private getErrorMessage(error: any): string {
    if (error.status === 0) {
      return "Unable to connect to the server. Please check your internet connection.";
    }

    if (error.status === 401) {
      return "Authentication failed. Please check your credentials.";
    }

    if (error.status === 403) {
      return "Access denied. You do not have permission to perform this action.";
    }

    if (error.status === 404) {
      return "The requested resource was not found.";
    }

    if (error.status === 500) {
      return "Server error. Please try again later.";
    }

    return error.message || "An unexpected error occurred. Please try again.";
  }
}
